import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { comparer } from 'mobx'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useGetReceiverModels } from '../../../../../../hooks/useGetReceiverModels'
import { useInvalidateQueries } from '../../../../../../hooks/useInvalidateQueries'
import { changeDataClarifyServiceRequest } from '../../../../../../services/NewApiService'
import {
  CheckEquipmentEverywhereRes,
  CheckReplacedEquipmentEverywhereRes,
} from '../../../../../../types/equipment.types'
import { MaintenanceOrder } from '../../../../../../types/service.types'
import { getModelId } from '../../../../../RequestsManagement/components/InstalationRequestDrawer/hooks/useInstallationDataNew'
import {
  ChangeEquipmentForm,
  changeEquipmentSchema,
  getDefaultValues,
  getReplacementValue,
  ReplacementType,
} from '../helpers/newChangeEquipment.helpers'

export const useNewChangeEquipment = (
  updateCurrentServiceData: (data: MaintenanceOrder) => void,
  request?: MaintenanceOrder,
  defaultEditReplaced?: boolean,
) => {
  const [isEdit, setIsEdit] = useState(false)
  const [isEquipmentAllowed, setIsEquipmentAllowed] = useState(true)
  const [lastCheckedId, setLastCheckedId] = useState(
    request?.oldEquipmentSerialNumber
      ? (request?.oldEquipmentSerialNumber ?? '')
      : (request?.equipmentId ?? ''),
  )
  const [lastReplacedCheckedId, setLastReplacedCheckedId] = useState(
    request?.oldEquipmentSerialNumber ? (request?.equipmentId ?? '') : '',
  )

  const invalidate = useInvalidateQueries()
  const { data: receiveresModels } = useGetReceiverModels()
  const defaultValues = useMemo(() => getDefaultValues(request), [request])
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    trigger,
    reset,
    setError,
    clearErrors,
  } = useForm<ChangeEquipmentForm>({
    defaultValues,
    resolver: zodResolver(changeEquipmentSchema),
    mode: 'onChange',
  })

  const replacementType = watch('replacementType') as ReplacementType
  const isOnlySn = replacementType === 'Замена только приемника'
  const isOnlyEquipment = replacementType === 'Замена только смарт-карты'
  const isSnAndEq = replacementType === 'Замена приемника и смарт-карты'
  const isNoReplace = replacementType === 'Не было замены'

  const onReset = () => {
    reset(defaultValues)
    setLastCheckedId(
      request?.oldEquipmentSerialNumber
        ? (request?.oldEquipmentSerialNumber ?? '')
        : (request?.equipmentId ?? ''),
    )
    setIsEdit(false)
    trigger()
    setLastReplacedCheckedId(
      request?.oldEquipmentSerialNumber ? (request?.equipmentId ?? '') : '',
    )
    setIsEquipmentAllowed(true)
  }

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['edit-service-data'],
    mutationFn: (body: any) =>
      changeDataClarifyServiceRequest(request?.id!, body),
    onSuccess: (res) => {
      updateCurrentServiceData(res)
      setIsEdit(false)
      invalidate([
        'service-table',
        'get-request-linked-to-service',
        'single-service-report-table',
        'get-service-history',
      ])
    },
  })

  const onSubmit = async (data: ChangeEquipmentForm) => {
    const body: Record<string, any> = {}

    if (data.equipmentId !== defaultValues.equipmentId)
      body.equipmentId = data.equipmentId
    if (data.receiverSerialNumber !== defaultValues.receiverSerialNumber) {
      body.receiverSerialNumber = data.receiverSerialNumber
      body.receiverModelId = getModelId(data.receiverModelId, receiveresModels)
    }
    if (isOnlyEquipment || isSnAndEq) {
      body.replacedEquipmentId = data.replacedEquipmentId
    }
    if (data.replacementType) {
      body.replacementVariant = getReplacementValue(
        data.replacementType as ReplacementType,
      )
    }
    if (isOnlySn || isSnAndEq) {
      body.replacedReceiverSerialNumber = data.replacedReceiverSerialNumber
      body.replacedReceiverModelId = getModelId(
        data.replacedReceiverModelId!,
        receiveresModels,
      )
    }

    await mutateAsync(body)
  }

  useEffect(() => {
    onReset()
  }, [defaultValues])

  const onEdit = () => setIsEdit((prev) => !prev)

  const canEditReplaced =
    defaultEditReplaced ||
    ([6.2, 14].includes(Number(request?.status) ?? 0) &&
      request?.dataCheckingStatus === 2)
  const canEditSn =
    defaultEditReplaced ||
    [8].includes(request?.status as number) ||
    canEditReplaced
  const showReplacedFields = canEditReplaced
  const isEquipmentIdChecked =
    lastCheckedId === watch('equipmentId') || !canEditSn

  const onCheckEquipment = async (
    data?: CheckEquipmentEverywhereRes,
    isReplaced?: boolean,
  ) => {
    const key = isReplaced ? 'replacedEquipmentId' : 'equipmentId'

    if (data?.specs) {
      if (isReplaced) {
        setValue('replacedHasCas', !!data.specs.hasCas)
        setValue('replacedReceiverSerialNumber', data.specs.sn!)
        setValue('replacedReceiverModelId', data.specs.model ?? '')
      } else {
        setValue('hasCas', !!data.specs.hasCas)
        setValue('receiverSerialNumber', data.specs.sn!)
        setValue('receiverModelId', data.specs.model ?? '')
      }
    } else {
      setValue(isReplaced ? 'replacedHasCas' : 'hasCas', false)
    }
    if (
      data?.specs ||
      watch(key) === watch(isReplaced ? 'replacedSmartCardId' : 'smartCardId')
    ) {
      setValue(isReplaced ? 'replacedSmartCardId' : 'smartCardId', null)
      clearErrors(isReplaced ? 'replacedSmartCardId' : 'smartCardId')
    }

    trigger()
    if (isReplaced) setLastReplacedCheckedId(watch('replacedEquipmentId')!)
    else setLastCheckedId(watch('equipmentId')!)
  }

  const onCheckReplacedEquipment = async (
    data?: CheckReplacedEquipmentEverywhereRes,
  ) => {
    if (!data?.isEmbedded && data?.isFree) {
      setIsEquipmentAllowed(true)
      trigger()
    } else {
      setIsEquipmentAllowed(false)
    }
    setLastReplacedCheckedId(watch('replacedEquipmentId')!)
  }

  useEffect(() => {
    if (request?.oldEquipmentSerialNumber) {
      setLastCheckedId(
        request?.oldEquipmentSerialNumber
          ? (request?.oldEquipmentSerialNumber ?? '')
          : (request?.equipmentId ?? ''),
      )
    }
  }, [request?.oldEquipmentSerialNumber])

  useEffect(() => {
    if (lastCheckedId && lastCheckedId === watch('equipmentId')) {
      setLastCheckedId(defaultValues.equipmentId)
    }
  }, [watch('equipmentId')])

  useEffect(() => {
    if (watch('receiverModelId')) trigger()
  }, [watch('receiverModelId')])

  useEffect(() => {
    if (watch('smartCardId') === watch('equipmentId')) {
      setValue('smartCardId', null)
      setValue('hasCas', true)
      trigger()
    }
  }, [watch('smartCardId')])

  useEffect(() => {
    if (replacementType !== defaultValues.replacementType) {
      setValue('replacedEquipmentId', '')
      setValue('replacedHasCas', false)
      setValue('replacedReceiverSerialNumber', '')
      setValue('replacedReceiverModelId', '')
      setLastReplacedCheckedId('')
    } else {
      setValue('replacedEquipmentId', defaultValues.replacedEquipmentId)
      setValue('replacedHasCas', defaultValues.replacedHasCas)
      setValue(
        'replacedReceiverSerialNumber',
        defaultValues.replacedReceiverSerialNumber,
      )
      setValue('replacedReceiverModelId', defaultValues.replacedReceiverModelId)
      setLastReplacedCheckedId(defaultValues.replacedEquipmentId ?? '')
    }
    setLastReplacedCheckedId(defaultValues.replacedEquipmentId ?? '')
    trigger()
  }, [replacementType])

  useEffect(() => {
    if (!isEdit) onReset()
  }, [isEdit])

  useEffect(() => {
    if (
      watch('receiverSerialNumber') === request?.receiverSerialNumber &&
      errors.receiverSerialNumber?.message === 'not found receiver'
    ) {
      clearErrors('receiverSerialNumber')
      trigger()
    }
  }, [errors.receiverSerialNumber, isEdit])

  useEffect(() => {
    if (
      watch('replacedReceiverSerialNumber') ===
        request?.replacedReceiverSerialNumber &&
      errors.replacedReceiverSerialNumber?.message === 'not found receiver'
    ) {
      clearErrors('replacedReceiverSerialNumber')
      trigger()
    }
  }, [errors.replacedReceiverSerialNumber, isEdit])

  const isReplacedEquipmentIdChecked =
    lastReplacedCheckedId === watch('replacedEquipmentId') ||
    (!isOnlyEquipment && !isSnAndEq)

  const disableReplacedFields =
    !!(!isOnlySn && !isOnlyEquipment) && watch('replacedHasCas')
  const wasReplacedErr =
    !!(!isOnlySn && !isSnAndEq) && !!errors.replacedReceiverSerialNumber
  const isAtLeastOneChanged = !comparer.structural(
    getDefaultValues(request),
    watch(),
  )

  return {
    isEdit,
    handleSubmit,
    onEdit,
    onSubmit,
    control,
    onCheckEquipment,
    canEditSn,
    watch,
    setValue,
    setError,
    canEditReplaced,
    showReplacedFields,
    setLastCheckedId,
    isValid:
      isValid &&
      !errors.receiverSerialNumber &&
      !wasReplacedErr &&
      isAtLeastOneChanged,
    isEquipmentIdChecked,
    isReplacedEquipmentIdChecked,
    isPending,
    onReset,
    disableReplacedFields,
    lastCheckedId,
    lastReplacedCheckedId,
    errors,
    isOnlyEquipment,
    isOnlySn,
    isNoReplace,
    isSnAndEq,
    onCheckReplacedEquipment,
    isEquipmentAllowed,
  }
}
